import { BoxProgramTemplateCalendarEvent } from "@/modules/boxProgrammation/components/boxProgramTemplateCalendarEvent"
import { useAllBoxSlotTemplates } from "@/modules/boxSlotTemplate/hooks/useBoxSlotTemplates"
import { useBoxSlotTemplatesBeingCreated } from "@/modules/boxSlotTemplate/store/useBoxSlotTemplateStore"
import { cn } from "@/modules/lib/utils"
import { useBoxSlotTemplateSidebar } from "@modules/boxSlotTemplate/hooks/useBoxSlotTemplateSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { Calendar } from "@modules/calendar/calendar"
import { type SlotTemplateEventType } from "@modules/calendar/calendarTypes"
import { useCallback, useMemo } from "react"
import { useParams } from "react-router"

type BoxProgramTemplateCalendarProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxProgramTemplateCalendar({ className, ...props }: BoxProgramTemplateCalendarProps) {
  const canAddSlotTemplate = useBoxStaffPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK)
  const canEditSlotTemplate = useBoxStaffPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK)
  const { navigateToBoxSlotTemplateEdition, navigateToBoxSlotTemplateCreation } = useBoxSlotTemplateSidebar()

  const { boxProgramId } = useParams()
  const { data: boxAllSlotTemplates } = useAllBoxSlotTemplates()
  const boxSlotTemplatesBeingCreated = useBoxSlotTemplatesBeingCreated()

  const filteredSlotTemplates = useMemo(() => {
    const createdIds = new Set(boxSlotTemplatesBeingCreated.map((slotTemplate) => slotTemplate.id))
    const filteredBaseSlotTemplates = Object.values(boxAllSlotTemplates.week)
      .flat()
      .filter((slotTemplate) => !createdIds.has(slotTemplate.id))
    return [
      ...filteredBaseSlotTemplates.map((slotTemplate) => slotTemplate.toCalendarEventTypeWithHour()),
      ...boxSlotTemplatesBeingCreated.map((slotTemplate) => slotTemplate.toCalendarEventTypeWithHour()),
    ]
  }, [boxAllSlotTemplates, boxSlotTemplatesBeingCreated])

  const isEventFromCurrentProgrammation = useCallback(
    (event: SlotTemplateEventType) => {
      return event.programId && boxProgramId ? event.programId === boxProgramId : false
    },
    [boxProgramId],
  )

  const isEventOverlayed = useCallback(
    (event: SlotTemplateEventType) => {
      return boxSlotTemplatesBeingCreated.length > 0
        ? boxSlotTemplatesBeingCreated.findIndex(
            (boxSlotTemplateBeingCreated) => boxSlotTemplateBeingCreated.id === event.id,
          ) >= 0
        : isEventFromCurrentProgrammation(event)
    },
    [boxSlotTemplatesBeingCreated, isEventFromCurrentProgrammation],
  )

  const onAddEventClick = (value?: Date | number) => {
    if (value && typeof value === "object" && boxProgramId) navigateToBoxSlotTemplateCreation(boxProgramId, value)
  }

  return (
    <div className={cn("pointer-events-auto flex grow flex-col content-center", className)} {...props}>
      <Calendar
        calendarEvents={filteredSlotTemplates}
        canAddEvents={canAddSlotTemplate}
        onAddEventClick={onAddEventClick}
        customRenderEvent={(_, event) => {
          return (
            <BoxProgramTemplateCalendarEvent
              event={event as SlotTemplateEventType}
              key={event.id}
              onClickEvent={() => boxProgramId && navigateToBoxSlotTemplateEdition(boxProgramId, event.id)}
              canBeClicked={
                isEventFromCurrentProgrammation(event as SlotTemplateEventType) &&
                boxSlotTemplatesBeingCreated.findIndex((slotTemplate) => slotTemplate.id === event.id) < 0 &&
                canEditSlotTemplate
              }
              isOverlayed={isEventOverlayed(event as SlotTemplateEventType)}
            />
          )
        }}
      />
    </div>
  )
}
